import {
  type AuthAccountWithPassType,
  retrieveAccounts,
  retrieveFavoritePlans,
  retrieveUser,
  retrieveUserPersistentState,
  updateUser,
} from 'api-client/accounts';
import { retrieveDismissibleNotices } from 'api-client/dismissibleNotices';
import type { AxiosError } from 'axios';
import type { AccountType } from 'bc-model';
import { getNoticeRegistry } from 'components/DismissibleNotices/noticeRegistry';
import apiAction, { handleApiLifecycle } from 'stores/actions/api';
import { includeBasePath } from 'utils/url-utils';
import type { StoreApi } from 'zustand';
import { combine, devtools, subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { createWithEqualityFn } from 'zustand/traditional';
import { fastGroupById } from '../utils/fastGroupById';
import { isUuidString } from 'bc-model';
import { previewVersion } from './compare/versions';
import { getFeatureFlag } from '../featureFlags';
import pMap from 'p-map';
import chunk from 'lodash/chunk';

type AccountStateType = AuthAccountWithPassType & {
  favoritePlans?: Set<number>;
};

type StateType = {
  /**
   * The currently logged-in user's account.
   */
  account: AccountStateType | null;
  accounts: Record<string | number, AuthAccountWithPassType>;
  /**
   * A map of dismissible notices. The key is the notice key and the value is a boolean indicating
   * whether the notice has been dismissed.
   */
  dismissibleNotices: { [key: string]: boolean };
  loading: { [key: string]: boolean };
  failure: { [key: string]: AxiosError };
  /**
   * Persistent state for the current user. This should not be accessed directly and the
   * `usePersistentValue` and `useSetPersistentValue` hooks should be used instead.
   */
  persistentState: { [key: string]: any };
  persistentStateLoaded: boolean;
};

type ActionType = {
  clearUser: () => void;
  load: () => void;
  loadAccount: (id: string | number) => AccountType | null;
  loadAccounts: (ids: Array<string | number>) => void;
  unload: () => void;
  updateUser: (user: AuthAccountWithPassType) => void;
  loadAccountData: () => void;
  updateAccountFavoritePlans: (favoritePlans: Set<number>) => void;
};

type AccountStoreType = StateType & ActionType;

const unloadedState = {
  account: null,
  accounts: {},
  dismissibleNotices: {
    dummyOnScreenNotice: true,
    dummyReleaseNotesNotice: true,
    dummyTipsAndTricksNotice: true,
    dummyWhatsNewNotice: true,
  },
  failure: {},
  loading: { init: true },
  persistentState: {},
  persistentStateLoaded: false,
};

const loadAssociatedAccountData = async (set: StoreApi<StateType>['setState']): Promise<void> => {
  const persistentState = await retrieveUserPersistentState();
  const persistentStateObj = persistentState.data.reduce((acc, cur) => {
    acc[cur.key] = cur.value;
    return acc;
  }, {});

  const dismissibleNotices = await retrieveDismissibleNotices();
  const dismissibleNoticesObj = dismissibleNotices.data.reduce((acc, cur) => {
    acc[cur.key] = cur.viewed;
    return acc;
  }, {});

  // Add any notices that are not in the database for this user
  Object.entries(getNoticeRegistry()).forEach(([key, notice]) => {
    if (!dismissibleNoticesObj[key]) {
      // Mark debug notices as having already been viewed, so they don't automatically show up
      dismissibleNoticesObj[key] = notice.isDebugNotice;
    }
  });

  const favoritePlansResponse = await retrieveFavoritePlans();
  const favoritePlans = favoritePlansResponse.data ?? [];

  set((state) => ({
    ...state,
    account: state.account
      ? {
          ...state.account,
          favoritePlans: new Set(favoritePlans),
        }
      : state.account,
    dismissibleNotices: dismissibleNoticesObj,
    persistentState: persistentStateObj,
    persistentStateLoaded: true,
  }));
};

// TODO[PIBE-246]: collapse to uuid-only once id columns are removed
const ACCOUNT_BATCH_SIZE_ID = 50;
const ACCOUNT_BATCH_SIZE_UUID = 35;

const buildBatchingAccountLoader = (fn: (data: AccountType[]) => void) => {
  let pending = {};
  let timeout: string | number | NodeJS.Timeout | undefined;
  return (id: string | number) => {
    if (pending[id]) {
      return;
    }
    pending[id] = true;

    if (timeout) {
      clearTimeout(timeout);
    }

    timeout = setTimeout(() => {
      const ids = Object.keys(pending).map((pendingId) => {
        const num = Number(pendingId);
        return Number.isNaN(num) ? pendingId : num;
      });
      pending = {};
      const useUuid = getFeatureFlag('USE_ACCOUNT_UUID') ?? false;
      const batchSize = useUuid ? ACCOUNT_BATCH_SIZE_UUID : ACCOUNT_BATCH_SIZE_ID;
      const idBatches = chunk(ids, batchSize);
      void pMap(
        idBatches,
        async (batch) => {
          const response = await retrieveAccounts(batch);
          return response.data;
        },
        { concurrency: 5 }
      )
        .then((responses) => {
          fn(responses.flat());
        })
        .catch((error) => {
          console.error('Failed to load accounts', error);
        });
    }, 100);
  };
};

const storeActions = (
  set: StoreApi<StateType>['setState'],
  get: StoreApi<StateType>['getState']
): ActionType => {
  const batchLoadAccount = buildBatchingAccountLoader(
    (data: (AccountType & { id?: number; uuid?: string })[]) => {
      set({
        accounts: {
          ...get().accounts,
          ...fastGroupById(data),
        },
      });
    }
  );
  return {
    clearUser: () => {
      set((state) => {
        return { ...state, account: null };
      });
    },
    // Load another user's account
    loadAccount: (id: string | number) => {
      const account = get().accounts[id];

      if (!account) {
        batchLoadAccount(id);
      }

      return account;
    },
    loadAccounts: (ids: Array<string | number>) => {
      const uniqueIds = Array.from(new Set(ids));
      const useUuid = getFeatureFlag('USE_ACCOUNT_UUID') ?? false;
      const batchSize = useUuid ? ACCOUNT_BATCH_SIZE_UUID : ACCOUNT_BATCH_SIZE_ID;
      const idBatches = chunk(uniqueIds, batchSize);
      void pMap(
        idBatches,
        async (batch) => {
          const response = await retrieveAccounts(
            batch.map((id) => (isUuidString(id) ? id : Number(id)))
          );
          return response.data;
        },
        { concurrency: 5 }
      )
        .then((responses) => {
          set({
            accounts: { ...get().accounts, ...fastGroupById(responses.flat()) },
          });
        })
        .catch((error) => {
          console.error('Failed to load accounts', error);
        });
    },
    // Load current user's account
    load: apiAction(
      retrieveUser,
      handleApiLifecycle(
        set,
        () => (get()?.account === unloadedState.account ? 'init' : 'update'),
        async (state, account, isModified) => {
          const initial = get().account === unloadedState.account;
          if (initial || isModified) {
            state.account = account;

            // Redirect to expired password screen
            if (
              account?.requirePasswordChange &&
              !window.location.href.includes('/login/expired') &&
              !window.location.href.includes('/logout')
            ) {
              window.location.href = includeBasePath(`/login/expired`);
            }
          }
          void loadAssociatedAccountData(set);
          // Reset store
          state.loading = {};
          state.failure = {};
        }
      )
    ),
    unload: () => {
      set(() => {
        return unloadedState;
      });
    },
    updateUser: apiAction(
      async (user: AuthAccountWithPassType) => {
        const useUuid = getFeatureFlag('USE_ACCOUNT_UUID') ?? false;
        let identifier: string | number | undefined;
        if (useUuid) {
          identifier = user.uuid ?? undefined;
        } else {
          identifier = user.id;
        }
        return updateUser(user, identifier);
      },
      handleApiLifecycle(
        set,
        'account',
        (state, userUpdated) => {
          if (userUpdated) {
            state.account = userUpdated;
          }
        },
        (state, userUpdating) => {
          Object.assign(state.account, userUpdating, previewVersion(state.account));
        }
      )
    ),
    loadAccountData: () => {
      void loadAssociatedAccountData(set);
    },
    updateAccountFavoritePlans: (favoritePlans: Set<number>) => {
      set((state) => ({
        // eslint-disable-next-line prefer-object-spread
        account: Object.assign({}, state.account, {
          favoritePlans,
        }),
      }));
    },
  };
};

const useAccountStore = createWithEqualityFn(
  devtools(
    subscribeWithSelector(immer(combine<StateType, ActionType>(unloadedState, storeActions))),
    {
      name: 'accountsStore',
    }
  )
);

export default useAccountStore;
export { useAccountStore as accountStore, loadAssociatedAccountData };
export type { AccountStoreType, StateType };
